import { useState } from 'react';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import InsightsIcon from '@mui/icons-material/Insights';
import { useUserContext } from '@/contexts/userContext';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { formatRelative } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import TabPanel from '@/components/tabPanel';
import EntityAccessControl from '@/components/entities/administration/accessControl/accessControl';
import AuditRecords from '@/components/entities/administration/auditRecords/auditRecords';
import UserAppUsageStats from './usageStats';

type Props = { userAppInstance: any; isOpen: boolean; maxHeight?: number };

const UserAppInstanceAdministration = ({ userAppInstance = {}, isOpen }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const apiContext = useApiContext();
  const confirm = useConfirm();

  const entityBase = `${userAppInstance?.entityType}:${userAppInstance?.entityDefId}`;

  const canDelete = hasPermission(`pipeline:apps:delete`, userAppInstance?.userAccess);

  const [selectedTab, setSelectedTab] = useState<any>(
    hasPermission(`pipeline:apps:accessControl:view`, userAppInstance?.userAccess)
      ? 'accessControl'
      : hasPermission(`pipeline:apps:usageStats:view`, userAppInstance?.userAccess)
        ? 'usageStats'
        : 'auditTrail',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const deleteUserAppInstance = async () => {
    confirm({ description: 'Are you sure you want to delete this user app instance?' }).then(async () => {
      try {
        setLoading(true);
        await apiContext.deleteUserAppInstance(userAppInstance.entityDefId, userAppInstance.id);
        const from = location.state?.from || '/instances?tab=userAppInstances';
        navigate(from);
      } catch (error) {
        setError(error);
      }
      setLoading(false);
    });
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  // Safe date formatting helpers
  const formatSafeDate = (date: any) => {
    if (!date) return '';
    try {
      return formatRelative(new Date(date), new Date());
    } catch (e) {
      return '';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Entity Info Bar */}
      <Box
        sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        {error && <ErrorMessage error={error} />}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t('entity.administration.nameLine', {
                name: userAppInstance?.name,
                type: userAppInstance?.userAppDef?.name,
              })}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {userAppInstance?.createdDate && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('entity.administration.createdLine', {
                    date: formatSafeDate(userAppInstance?.createdDate),
                    name: userAppInstance?.createdBy,
                  })}
                </Typography>
              )}
              {userAppInstance?.lastUpdatedDate && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('entity.administration.lastUpdatedLine', {
                    date: formatSafeDate(userAppInstance?.lastUpdatedDate),
                    name: userAppInstance?.lastUpdatedBy,
                  })}
                </Typography>
              )}
            </Stack>
          </Stack>
          {canDelete && (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={deleteUserAppInstance}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete Instance
            </Button>
          )}
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="secondary" sx={{ minHeight: 40 }}>
          {hasPermission(`pipeline:apps:accessControl:view`, userAppInstance?.userAccess) && (
            <Tab
              value="accessControl"
              icon={<ShareIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              sx={{ minHeight: 40, textTransform: 'none' }}
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'accessControl' ? 700 : 500 }}>
                  Access Control
                </Typography>
              }
            />
          )}
          {hasPermission(`pipeline:apps:usageStats:view`, userAppInstance?.userAccess) && (
            <Tab
              value="usageStats"
              icon={<InsightsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              sx={{ minHeight: 40, textTransform: 'none' }}
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'usageStats' ? 700 : 500 }}>
                  Usage Stats
                </Typography>
              }
            />
          )}
          {hasPermission(`pipeline:apps:auditTrail:view`, userAppInstance?.userAccess) && (
            <Tab
              value="auditTrail"
              icon={<InsightsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              sx={{ minHeight: 40, textTransform: 'none' }}
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'auditTrail' ? 700 : 500 }}>
                  Audit Trail
                </Typography>
              }
            />
          )}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 1 }}>
        {isOpen && (
          <>
            {hasPermission(`pipeline:apps:accessControl:view`, userAppInstance?.userAccess) && (
              <TabPanel value="accessControl" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <EntityAccessControl entity={userAppInstance} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:apps:usageStats:view`, userAppInstance?.userAccess) && (
              <TabPanel value="usageStats" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <UserAppUsageStats entity={userAppInstance} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:apps:auditTrail:view`, userAppInstance?.userAccess) && (
              <TabPanel value="auditTrail" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <AuditRecords entity={userAppInstance} />
              </TabPanel>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default UserAppInstanceAdministration;
